import { useCallback, useEffect, useState } from "react";
import type {
  ContractId,
  MissionView,
  Profile,
} from "@frontier/protocol/contracts";
import { focusedMission, navigationSource } from "./mission-focus";
import { browserAuthStorage } from "./auth-storage";

export function useMissionFocus(
  profile: Profile | null,
  missions: readonly MissionView[],
  trackedEventId: string | null = null,
) {
  const [focus, setFocus] = useState({ playerId: "", value: "", navigation: "", pendingEvent: "" });
  const [storage] = useState(browserAuthStorage);
  const playerId = profile?.playerId ?? "";
  useEffect(() => {
    setFocus({
      playerId,
      value: playerId
        ? (storage.getItem(`frontier.mission-focus.${playerId}`) ?? "")
        : "",
      navigation: playerId
        ? (storage.getItem(`frontier.navigation-focus.${playerId}`) ?? "")
        : "",
      pendingEvent: "",
    });
  }, [playerId, storage]);
  const training = profile?.tutorial?.version === 2 && !profile.tutorial.docked
    ? missions.find(entry => entry.runId === profile.runId && entry.contractId === "intro") : undefined;
  const mission = training ?? focusedMission(
    missions,
    profile?.runId,
    focus.playerId === playerId ? focus.value : "",
  );
  const focusContract = useCallback(
    (contractId: ContractId) => {
      if (
        !profile ||
        !missions.some(
          (entry) =>
            entry.runId === profile.runId && entry.contractId === contractId,
        )
      )
        return;
      const focused = missions.find(
        (entry) =>
          entry.runId === profile.runId && entry.contractId === contractId,
      );
      if (!focused) return;
      const value = `${profile.runId}/${focused.orderId}`;
      setFocus({ playerId: profile.playerId, value, navigation: value, pendingEvent: "" });
      storage.setItem(`frontier.mission-focus.${profile.playerId}`, value);
    },
    [profile, missions, storage],
  );
  const focusEvent = useCallback((eventId: string) => {
    if (!profile) return;
    const value = eventId
      ? `${profile.runId}/event/${eventId}`
      : mission ? `${profile.runId}/${mission.orderId}` : "";
    setFocus(current => {
      const confirmed = !eventId || trackedEventId === eventId;
      return {
        playerId: profile.playerId,
        value: current.playerId === profile.playerId ? current.value : "",
        navigation: confirmed ? value : current.navigation,
        pendingEvent: confirmed ? "" : value,
      };
    });
  }, [profile, mission, trackedEventId]);
  useEffect(() => {
    if (!profile || !trackedEventId) return;
    const confirmed = `${profile.runId}/event/${trackedEventId}`;
    setFocus(current => current.playerId === profile.playerId && current.pendingEvent === confirmed
      ? { ...current, navigation: confirmed, pendingEvent: "" }
      : current);
  }, [profile?.playerId, profile?.runId, trackedEventId]);
  useEffect(() => {
    if (playerId && focus.playerId === playerId)
      storage.setItem(`frontier.navigation-focus.${playerId}`, focus.navigation);
  }, [playerId, focus.playerId, focus.navigation, storage]);
  return {
    mission,
    focusContract,
    focusEvent,
    navigationSource: training ? "contract" : navigationSource(
      mission,
      focus.playerId === playerId ? focus.navigation : "",
      trackedEventId,
    ),
  };
}
